import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import QRCode from "qrcode";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import TotpCodeField from "./TotpCodeField";
import {
  TwoFactorSetup,
  TwoFactorStatus,
  disableTwoFactor,
  enableTwoFactor,
  getTwoFactorStatus,
  setupTwoFactor,
} from "../utils/api";

/** Разбирает detail бэкенда: он приходит и строкой, и объектом с locked_until. */
const describeError = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Не удалось выполнить запрос";
  if (typeof detail === "string") return detail;
  if (detail.locked_until) {
    const until = new Date(detail.locked_until).toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `Слишком много неверных кодов. Повторите после ${until}`;
  }
  return detail.message || "Не удалось выполнить запрос";
};

const TwoFactorSection: React.FC<{
  onNotify?: (message: string, severity: "success" | "error") => void;
}> = ({ onNotify }) => {
  const theme = useTheme();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const notify = (message: string, severity: "success" | "error") =>
    onNotify?.(message, severity);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTwoFactorStatus();
      setStatus(res.data);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** otpauth-ссылку рисуем в QR локально — наружу секрет не уходит. */
  useEffect(() => {
    if (!setup?.otpauth_url) {
      setQr(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(setup.otpauth_url, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#0B1020", light: "#FFFFFF" },
    })
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setup]);

  const startSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await setupTwoFactor();
      setSetup(res.data);
      setCode("");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      await enableTwoFactor(code);
      notify("Двухфакторная аутентификация включена", "success");
      setSetup(null);
      setCode("");
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      await disableTwoFactor(disableCode);
      notify("Двухфакторная аутентификация выключена", "success");
      setDisableOpen(false);
      setDisableCode("");
      await load();
    } catch (err) {
      const message = describeError(err);
      setError(message);
      notify(message, "error");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (!setup) return;
    navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const enabled = Boolean(status?.enabled);

  return (
    <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: "20px", mb: 2.5 }}>
      <Stack direction="row" spacing={1.75} alignItems="center" mb={2.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: "13px",
            color: enabled
              ? theme.palette.semanticText.success
              : theme.palette.accentText,
            bgcolor: alpha(
              enabled ? theme.palette.success.main : theme.palette.primary.main,
              0.11,
            ),
          }}
        >
          <ShieldOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: 17 }}>
              Двухфакторная аутентификация
            </Typography>
            {!loading && (
              <Chip
                size="small"
                label={enabled ? "Включена" : "Выключена"}
                sx={{
                  fontWeight: 700,
                  color: enabled
                    ? theme.palette.semanticText.success
                    : theme.palette.text.secondary,
                  backgroundColor: alpha(
                    enabled ? theme.palette.success.main : "#667085",
                    0.12,
                  ),
                }}
              />
            )}
          </Stack>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            Одноразовый код из приложения в дополнение к паролю
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {enabled && (
            <Stack spacing={2}>
              <Alert severity="success" icon={<ShieldOutlined fontSize="small" />}>
                Вход защищён кодом. При утере доступа к приложению снять защиту
                может только администратор — резервных кодов в системе нет.
              </Alert>
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisableOpen(true)}
                >
                  Отключить
                </Button>
              </Box>
            </Stack>
          )}

          {!enabled && !setup && (
            <Stack spacing={2}>
              {status?.pending_setup && (
                <Alert severity="warning">
                  Настройка была начата, но не подтверждена кодом. Запустите её
                  заново — предыдущий секрет будет заменён.
                </Alert>
              )}
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                Понадобится приложение-аутентификатор: Google Authenticator, 1Password,
                Aegis или любое другое с поддержкой TOTP.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  onClick={startSetup}
                  disabled={busy}
                  startIcon={busy ? undefined : <ShieldOutlined />}
                >
                  {busy ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : status?.pending_setup ? (
                    "Настроить заново"
                  ) : (
                    "Включить"
                  )}
                </Button>
              </Box>
            </Stack>
          )}

          {!enabled && setup && (
            <Stack spacing={2.5}>
              <Typography sx={{ fontWeight: 650, fontSize: 14.5 }}>
                1. Отсканируйте QR-код в приложении
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
              >
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    flexShrink: 0,
                    borderRadius: "16px",
                    overflow: "hidden",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "#FFFFFF",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {qr ? (
                    <img
                      src={qr}
                      alt="QR-код для приложения-аутентификатора"
                      width={190}
                      height={190}
                    />
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
                    Не получается отсканировать — введите ключ вручную:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha("#FFFFFF", 0.04)
                          : alpha("#172033", 0.03),
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 13.5,
                        letterSpacing: "0.06em",
                        wordBreak: "break-all",
                        flex: 1,
                      }}
                    >
                      {setup.secret}
                    </Typography>
                    <Tooltip title={copied ? "Скопировано" : "Скопировать ключ"}>
                      <IconButton
                        aria-label="Скопировать ключ"
                        size="small"
                        onClick={copySecret}
                      >
                        {copied ? (
                          <CheckRounded
                            sx={{
                              fontSize: 16,
                              color: theme.palette.semanticText.success,
                            }}
                          />
                        ) : (
                          <ContentCopyRounded sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: 12, mt: 1 }}
                  >
                    Издатель: {setup.issuer}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Typography sx={{ fontWeight: 650, fontSize: 14.5 }}>
                2. Введите код из приложения, чтобы подтвердить
              </Typography>

              <Box sx={{ maxWidth: 280 }}>
                <TotpCodeField
                  value={code}
                  onChange={setCode}
                  autoFocus
                  disabled={busy}
                  onEnter={() => code.length >= 6 && confirmEnable()}
                />
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={confirmEnable}
                  disabled={code.length < 6 || busy}
                >
                  {busy ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Подтвердить и включить"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSetup(null);
                    setCode("");
                    setError(null);
                  }}
                  disabled={busy}
                >
                  Отмена
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}

      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Отключить двухфакторную аутентификацию</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="flex-start" mb={2.5}>
            <WarningAmberRounded color="error" fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>
              Вход снова будет защищён только паролем. Для подтверждения введите
              действующий код из приложения.
            </Typography>
          </Stack>
          <TotpCodeField
            value={disableCode}
            onChange={setDisableCode}
            autoFocus
            disabled={busy}
            onEnter={() => disableCode.length >= 6 && confirmDisable()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDisableOpen(false)}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDisable}
            disabled={disableCode.length < 6 || busy}
          >
            Отключить
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TwoFactorSection;
